import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, RefreshCw } from "lucide-react";
import {
  DashboardParams,
  useGetDashboardOverview,
} from "../../lib/api/dashboard";
import DashboardHeader from "../../components/admin/dashboard/DashboardHeader";
import QuickStatsGrid from "../../components/admin/dashboard/QuickStatsGrid";
import AssignmentStatusChart from "../../components/admin/dashboard/AssignmentStatusChart";
import UserStatusBreakdown from "../../components/admin/dashboard/UserStatusBreakdown";

const AdminDashboard: React.FC = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<DashboardParams["period"]>("month");

  // FIX: Pass period to the hook
  const {
    data: dashboardData,
    isLoading: isLoadingDashboard,
    isError: isDashboardError,
    error: dashboardError,
    isRefetching,
    refetch: refetchDashboard,
  } = useGetDashboardOverview({ period });

  // Handle period change
  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod as DashboardParams["period"]);
  };

  // Handle refresh
  const handleRefresh = async () => {
    // Remove stale queries first
    queryClient.removeQueries({ queryKey: ["dashboard-overview"] });
    queryClient.removeQueries({ queryKey: ["dashboard-top-projects"] });
    queryClient.removeQueries({ queryKey: ["dashboard-activities"] });

    // Then refetch
    await Promise.all([refetchDashboard()]);
  };

  // Get error message helper
  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === "string") {
      return error;
    }
    if (error && typeof error === "object" && "message" in error) {
      return String((error as { message: unknown }).message);
    }
    return "An error occurred while fetching dashboard data.";
  };

  // Error state
  if (isDashboardError && !dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="">
          <DashboardHeader
            period={period || "month"}
            onPeriodChange={handlePeriodChange}
            onRefresh={handleRefresh}
          />
          <div className="bg-white rounded-xl border border-red-200 p-8 text-center mt-6">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Failed to Load Dashboard
            </h2>
            <p className="text-gray-600 mb-4 text-sm max-w-md mx-auto">
              {getErrorMessage(dashboardError)}
            </p>
            <button
              onClick={handleRefresh}
              disabled={isRefetching}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw
                className={`w-4 h-4 ${isRefetching ? "animate-spin" : ""}`}
              />
              {isRefetching ? "Retrying..." : "Try Again"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="">
        {/* Header */}
        <DashboardHeader
          period={period || "month"}
          onPeriodChange={handlePeriodChange}
          onRefresh={handleRefresh}
          isRefreshing={isRefetching}
        />

        {/* Quick Stats Grid */}
        <div className="mb-6">
          <QuickStatsGrid data={dashboardData} loading={isLoadingDashboard} />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - 2/3 width on large screens */}
          <div className="lg:col-span-2 space-y-6">
            {/* Assignment Status */}
            <AssignmentStatusChart
              data={dashboardData?.assignments}
              loading={isLoadingDashboard}
            />

            {/* Top Projects */}
            {/* <TopProjectsList
              projects={topProjects}
              loading={isLoadingProjects}
            /> */}
          </div>

          {/* Right Column - 1/3 width on large screens */}
          <div className="space-y-6">
            {/* Revenue Card */}
            {/* <RevenueCard
              data={dashboardData?.revenue}
              loading={isLoadingDashboard}
            /> */}

            {/* User Status Breakdown */}
            <UserStatusBreakdown
              data={dashboardData?.users}
              loading={isLoadingDashboard}
            />

            {/* Quick Actions */}
            {/* <QuickActions /> */}

            {/* Recent Activity */}
            {/* <RecentActivityFeed
              activities={dashboardData?.recentActivities}
              loading={isLoadingDashboard}
            /> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
